import React, {
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
} from 'react';
import { supabase } from '../utils/supabaseClient';
import { User } from '@supabase/supabase-js';
import {
  setDarkTheme,
  setPreferredLanguage,
  setDefaultUserSettings,
} from '@/redux/userSlice';
import { resetAgentsState } from '@/redux/agentsSlice';
import { resetConversationsState } from '@/redux/conversationsSlice';
import { resetNotificationsState } from '@/redux/notificationsSlice';
import { useDispatch, useSelector } from 'react-redux';
import i18n from '@/i18n';
import { RootState } from '@/redux/store';
import {
  getAuthRedirectUrl,
  isRecoveryLinkInUrl,
  normalizeAuthError,
} from '@/utils/auth';
import { clearAppSessionHint, setAppSessionHint } from '@/utils/appSessionHint';
import { toSupportedLanguage } from '@/utils/supportedLanguages';
import { ACCOUNT_ROUTE } from '@/utils/appRoutes';

interface UserSettings {
  dark_theme: boolean;
  preferred_language: string;
}

interface AuthContextType {
  signUp: (data: {
    email: string;
    password: string;
    options?: {
      emailRedirectTo?: string;
      data?: Record<string, unknown>;
    };
  }) => Promise<{ user: User | null; error: Error | null }>;
  signIn: (data: {
    email: string;
    password: string;
  }) => Promise<{ user: User | null; error: Error | null }>;
  signOut: () => Promise<void>;
  user: User | null;
  fetchUserSettings: (userId?: string) => Promise<UserSettings | null>;
  updateUserSettings: (settings: Partial<UserSettings>) => Promise<void>;
  updatePassword: (newPassword: string) => Promise<Error | null>;
  /**
   * Starts an email change. Supabase sends a confirmation link; with "Secure
   * email change" on it goes to BOTH the old and new address, which is what
   * stops a hijacked session from quietly moving the account. Nothing changes
   * until the link is followed, so the caller should say so.
   */
  updateEmail: (newEmail: string) => Promise<Error | null>;
  /**
   * False for an account that only ever signed in through Google: there is no
   * password to re-authenticate with, so the email change is gated behind
   * setting one first.
   */
  hasPasswordIdentity: boolean;
  requestPasswordReset: (
    email: string,
    redirectTo: string
  ) => Promise<Error | null>;
  resendVerificationEmail: (
    email: string,
    redirectTo: string
  ) => Promise<Error | null>;
  signInWithGoogle: (options?: {
    options: { redirectTo: string };
  }) => Promise<void>;
  getJwt: () => Promise<string | null>;
  isRecoverySession: boolean;
  clearRecoverySession: () => void;
}

const AuthContext = React.createContext<AuthContextType | undefined>(undefined);

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const dispatch = useDispatch();
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isRecoverySession, setIsRecoverySession] = useState(false);
  const previousUserIdRef = useRef<string | null>(null);
  const darkTheme = useSelector(
    (state: RootState) => state.user.settings.darkTheme
  );

  const clearUserSessionData = useCallback(() => {
    dispatch(resetAgentsState());
    dispatch(resetConversationsState());
    dispatch(resetNotificationsState());
  }, [dispatch]);

  useEffect(() => {
    document.documentElement.classList.toggle('dark', darkTheme);
  }, [darkTheme]);

  // Tells the landing page on dialup.lv whether to send this browser to the
  // app or to the login screen. Waits for the initial session lookup so a
  // signed-in user is not briefly reported as signed out.
  useEffect(() => {
    if (loading) return;
    if (user) setAppSessionHint();
    else clearAppSessionHint();
  }, [user, loading]);

  const fetchUserSettings = async (
    userId?: string
  ): Promise<UserSettings | null> => {
    const targetUserId = userId ?? user?.id;
    if (!targetUserId) return null;
    const { data, error } = await supabase
      .from('user_settings')
      .select('*')
      .eq('id', targetUserId);

    if (error) {
      console.error('Error fetching user settings:', error);
      return null;
    }
    return data && data.length > 0 ? data[0] : null;
  };

  const applyUserSettings = async (settings: UserSettings) => {
    dispatch(setDarkTheme(settings.dark_theme));
    // The database enum holds eight languages; the UI ships two. A stored
    // value outside supportedLanguages (one account holds et_EE) would be
    // handed to i18next raw, which then has no bundle to load -- so clamp it
    // rather than leaving the app in a half-resolved state. A null is the
    // same problem: changeLanguage(null) is not a no-op.
    const language = toSupportedLanguage(settings.preferred_language);
    dispatch(setPreferredLanguage(language));
    i18n.changeLanguage(language);
  };

  useEffect(() => {
    let mounted = true;

    const hydrateUserSettings = (userId: string) => {
      void (async () => {
        const settings = await fetchUserSettings(userId);
        if (settings && mounted) {
          await applyUserSettings(settings);
        }
      })();
    };

    const setupSession = async () => {
      if (mounted) setIsRecoverySession(isRecoveryLinkInUrl());
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (session?.user && mounted) {
        previousUserIdRef.current = session.user.id;
        setUser(session.user);
        localStorage.setItem('user', JSON.stringify(session.user));
        hydrateUserSettings(session.user.id);
      }
      if (mounted) setLoading(false);
    };

    setupSession();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY' && mounted) {
        setIsRecoverySession(true);
      } else if (isRecoveryLinkInUrl() && mounted) {
        setIsRecoverySession(true);
      } else if (event === 'SIGNED_OUT' && mounted) {
        setIsRecoverySession(false);
      }

      if (session?.user && mounted) {
        const newUserId = session.user.id;
        if (
          previousUserIdRef.current !== null &&
          previousUserIdRef.current !== newUserId
        ) {
          clearUserSessionData();
        }
        previousUserIdRef.current = newUserId;
        setUser(session.user);
        localStorage.setItem('user', JSON.stringify(session.user));
        hydrateUserSettings(newUserId);
      } else if (mounted) {
        previousUserIdRef.current = null;
        setUser(null);
        localStorage.removeItem('user');
        dispatch(setDefaultUserSettings());
        clearUserSessionData();
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [dispatch, clearUserSessionData]);

  const value: AuthContextType = {
    signUp: async data => {
      const { email, password, options } = data;
      const { data: responseData, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: options?.emailRedirectTo,
          data: options?.data,
        },
      });
      return { user: responseData?.user || null, error };
    },
    signIn: async ({ email, password }) => {
      const { data: responseData, error } =
        await supabase.auth.signInWithPassword({ email, password });
      if (responseData?.user) {
        const newUserId = responseData.user.id;
        if (
          previousUserIdRef.current !== null &&
          previousUserIdRef.current !== newUserId
        ) {
          clearUserSessionData();
        }
        previousUserIdRef.current = newUserId;
        setUser(responseData.user);
        localStorage.setItem('user', JSON.stringify(responseData.user));
      }
      return { user: responseData?.user || null, error };
    },
    signInWithGoogle: async options => {
      await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: options?.options,
      });
    },
    signOut: async () => {
      previousUserIdRef.current = null;
      clearUserSessionData();
      dispatch(setDefaultUserSettings());
      setUser(null);
      setIsRecoverySession(false);
      localStorage.removeItem('user');
      await supabase.auth.signOut();
    },
    updateEmail: async newEmail => {
      try {
        const { error } = await supabase.auth.updateUser(
          { email: newEmail },
          { emailRedirectTo: getAuthRedirectUrl(ACCOUNT_ROUTE) }
        );
        return error;
      } catch (error) {
        return normalizeAuthError(
          error,
          'Unable to change the email address. Please try again.'
        );
      }
    },
    hasPasswordIdentity: Boolean(
      user?.identities?.some(identity => identity.provider === 'email')
    ),
    updatePassword: async newPassword => {
      try {
        const { error } = await supabase.auth.updateUser({
          password: newPassword,
        });
        return error;
      } catch (error) {
        return normalizeAuthError(
          error,
          'Unable to update password. Please try again.'
        );
      }
    },
    requestPasswordReset: async (email, redirectTo) => {
      try {
        const { error } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo,
        });
        return error;
      } catch (error) {
        return normalizeAuthError(
          error,
          'Unable to request password reset. Please try again.'
        );
      }
    },
    resendVerificationEmail: async (email, redirectTo) => {
      try {
        const { error } = await supabase.auth.resend({
          type: 'signup',
          email,
          options: {
            emailRedirectTo: redirectTo,
          },
        });
        return error;
      } catch (error) {
        return normalizeAuthError(
          error,
          'Unable to resend verification email. Please try again.'
        );
      }
    },
    user,
    isRecoverySession,
    clearRecoverySession: () => {
      setIsRecoverySession(false);
    },
    fetchUserSettings,
    updateUserSettings: async settings => {
      if (!user) return;
      // Upsert, not update: an account with no settings row matched nothing,
      // returned [], and then data[0] was dereferenced -- so saving a language
      // threw instead of saving. A trigger now creates the row at signup, but
      // the upsert keeps older accounts working without depending on the
      // backfill having run.
      const { data, error } = await supabase
        .from('user_settings')
        .upsert({ id: user.id, ...settings }, { onConflict: 'id' })
        .select();

      if (error) {
        console.error('Error saving user settings:', error);
        throw error;
      }
      if (data?.[0]) {
        await applyUserSettings(data[0] as UserSettings);
      }
    },
    getJwt: async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      return session?.access_token || null;
    },
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
